/**
 * usePresenceHeartbeat — 2026-10-12 (PHASE 1, shadow).
 *
 * Renews the agent's server-side presence lease on a steady cadence so the
 * backend can detect a real departure (tab close / laptop sleep / Wi-Fi drop)
 * within ~1 minute instead of the 16h janitor. See
 * PRESENCE-HEARTBEAT-LIFECYCLE-DESIGN.md (v2).
 *
 * Design notes:
 *  - Driven by a tiny inline Web Worker timer (same pattern as
 *    useScreenRecording) because Chrome heavily throttles setInterval in
 *    BACKGROUND tabs — and a backgrounded tab is EXACTLY the flapping cause we
 *    are trying to make resilient. The Worker keeps beating while hidden.
 *  - Only heartbeats while `active` (SIP REGISTERED). Stops cleanly otherwise.
 *  - On pagehide/beforeunload fires a best-effort sendBeacon `/presence/bye`
 *    (clean-close fast path). NEVER relied upon — the lease TTL is the backstop.
 *  - visibilitychange does NOT bye (switching/minimizing a tab is constant and
 *    the Worker heartbeat keeps the hidden tab present).
 *  - Phase 1: this only renews/clears the lease; the server-side sweeper is in
 *    shadow mode and changes no agent state yet.
 */
import { useEffect, useRef } from 'react';
import { softphoneApi } from '../api/client';

// Fallback cadence if the server doesn't tell us. Server authoritative value
// comes back from the first heartbeat ({heartbeat_sec}). Kept well under the
// 45s lease TTL so 1 dropped beat never expires the lease.
const DEFAULT_HEARTBEAT_MS = 15000;

function makeTimerWorker() {
  try {
    const src =
      'let t=null;' +
      'onmessage=function(e){' +
      ' var d=e.data||{};' +
      ' if(d.cmd==="start"){ if(t)clearInterval(t); var ms=d.ms||15000;' +
      '   t=setInterval(function(){postMessage({tick:1});}, ms); }' +
      ' else if(d.cmd==="stop"){ if(t){clearInterval(t);t=null;} }' +
      '};';
    const url = URL.createObjectURL(new Blob([src], { type: 'application/javascript' }));
    const w = new Worker(url);
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 2000);
    return w;
  } catch (err) {
    console.warn('[CX-PRESENCE] worker unavailable, using setInterval', err);
    return null;
  }
}

export function usePresenceHeartbeat(active) {
  const workerRef = useRef(null);
  const intervalRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    if (!active) return undefined;

    let stopped = false;

    const beat = async () => {
      if (stopped || sendingRef.current) return;
      sendingRef.current = true;
      try {
        const res = await softphoneApi.presenceHeartbeat();
        // Adopt the server's cadence if provided and different.
        const serverMs = res && res.heartbeat_sec ? res.heartbeat_sec * 1000 : null;
        if (serverMs && workerRef.current && serverMs !== beat._ms) {
          beat._ms = serverMs;
          try { workerRef.current.postMessage({ cmd: 'start', ms: serverMs }); } catch (_) {}
        }
      } catch (_) {
        // Best-effort: a missed heartbeat is fine (lease has grace). Don't spam.
      } finally {
        sendingRef.current = false;
      }
    };

    // Immediate first beat so the lease exists right away.
    beat();

    // Prefer the Worker timer (throttle-proof); fall back to setInterval.
    const w = makeTimerWorker();
    if (w) {
      workerRef.current = w;
      w.onmessage = (e) => { if (e && e.data && e.data.tick) beat(); };
      w.postMessage({ cmd: 'start', ms: DEFAULT_HEARTBEAT_MS });
    } else {
      intervalRef.current = setInterval(beat, DEFAULT_HEARTBEAT_MS);
    }

    // Clean-close fast path: pagehide/beforeunload -> bye beacon.
    const onBye = () => { try { softphoneApi.presenceBye(); } catch (_) {} };
    window.addEventListener('pagehide', onBye);
    window.addEventListener('beforeunload', onBye);

    return () => {
      stopped = true;
      if (workerRef.current) {
        try { workerRef.current.postMessage({ cmd: 'stop' }); } catch (_) {}
        try { workerRef.current.onmessage = null; workerRef.current.terminate(); } catch (_) {}
        workerRef.current = null;
      }
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      window.removeEventListener('pagehide', onBye);
      window.removeEventListener('beforeunload', onBye);
      // Note: we do NOT bye on normal unmount (e.g. route change within the SPA)
      // — only on actual page unload. A route change isn't a logout.
    };
  }, [active]);
}

export default usePresenceHeartbeat;
